import { useMemo, useState } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Input,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import styles from "./styles.module.scss";
import { useAtomValue } from "jotai";
import { userAtom } from "../../../utils/store";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  acceptAdmissionOffer,
  getDashboardStatus,
} from "../../../utils/request";
import { formatCurrency } from "../../../utils/currency";
import { usePayment } from "../../../contexts/payment";
import { APP_BASE_URL } from "../../../utils/constants";

type Status = "offered" | "accepted" | "rejected" | "pending";

const statusConfig: Record<Status, { label: string; color: string }> = {
  offered: { label: "Admission Offered", color: "geekblue" },
  accepted: { label: "Accepted", color: "green" },
  rejected: { label: "Rejected", color: "red" },
  pending: { label: "Pending", color: "gold" },
};

const AdmissionStatus = () => {
  const user = useAtomValue(userAtom);
  const { notification } = App.useApp();
  const { initiatePayment, isProcessing } = usePayment();
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const fullName = `${user?.firstName} ${user?.lastName}`;
  const acceptanceFee = user?.acceptanceFee ?? 0;
  const acceptanceFeeFormatted = formatCurrency(acceptanceFee);
  //const acceptanceFeeDeadline = "March 31, 2026";

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["dashboard-status"],
    queryFn: () => getDashboardStatus(user?.applicantId ?? ""),
  });

  const admissionStatus = data?.data as DashboardStatus;
  const hasPaidAcceptanceFee = admissionStatus?.hasPaidAcceptanceFee === true;
  const canPayAcceptanceFee = admissionStatus?.canPayAcceptanceFee === true;

  const acceptOfferMutation = useMutation({
    mutationFn: acceptAdmissionOffer,
    onSuccess: (data) => {
      notification.success({
        message: "Success",
        description:
          data?.message || "Your decision has been submitted successfully.",
      });
      refetch();
    },
    onError: (error: any) => {
      notification.error({
        message: "Error",
        description:
          error?.response?.data?.message ||
          "An error occurred while submitting your decision.",
      });
    },
  });

  // Derive admission offer status (whether admitted or not)
  const admissionOfferStatus: Status = useMemo(() => {
    if (!admissionStatus) return "pending";

    if (
      admissionStatus.isAdmitted === null ||
      admissionStatus.isAdmitted === 3
    ) {
      return "pending";
    } else if (admissionStatus.isAdmitted === 2) {
      return "rejected";
    } else {
      return "offered";
    }
  }, [admissionStatus]);

  // Determine if user has accepted admission
  const hasAccepted = useMemo(
    () => admissionStatus?.hasAcceptedAdmission === true,
    [admissionStatus],
  );

  // Determine if form should be shown (offered but not yet accepted)
  const isActionable = useMemo(
    () => admissionOfferStatus === "offered" && !hasAccepted,
    [admissionOfferStatus, hasAccepted],
  );

  const canReject = useMemo(
    () => isActionable && rejectionReason.trim().length > 4,
    [isActionable, rejectionReason],
  );

  const handleAccept = async () => {
    try {
      await acceptOfferMutation.mutateAsync({
        applicantId: user?.applicantId ?? "",
        decision: 1,
      });
      setAcceptModalOpen(true);
    } catch (error) {
      // Error handled in mutation onError
    }
  };

  const handleRejectSubmit = async () => {
    if (!canReject) return;

    try {
      await acceptOfferMutation.mutateAsync({
        applicantId: user?.applicantId ?? "",
        decision: 2,
        rejectionReason: rejectionReason,
      });
    } catch (error) {
      // Error handled in mutation onError
    }
  };

  const handleProcessPayment = async () => {
    const payload: ProcessPaymentPayloadWithChannel = {
      amount: 0,
      returnUrl: `${APP_BASE_URL}/payments`,
      applicantId: user?.applicantId ?? "",
      paymentType: 2,
    };
    initiatePayment(payload);
  };

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <Typography.Title level={2} className={styles.title}>
          Admission Status
        </Typography.Title>
        <Typography.Paragraph className={styles.subtitle}>
          Review your admission offer and respond at your convenience.
        </Typography.Paragraph>
      </div>

      <Card className={styles.card}>
        {isLoading ? (
          <div className={styles.loading}>
            <Spin size="large" />
            <Typography.Text>
              Fetching your admission details...
            </Typography.Text>
          </div>
        ) : isError ? (
          <div>
            {error instanceof Error ? error.message : "An error occurred"}
          </div>
        ) : (
          <div className={styles.content}>
            <div className={styles.statusRow}>
              <Typography.Title level={4} className={styles.sectionTitle}>
                Admission Status
              </Typography.Title>
              <Tag
                color={statusConfig[admissionOfferStatus].color}
                className={styles.statusTag}
              >
                {statusConfig[admissionOfferStatus].label}
              </Tag>
            </div>

            {admissionOfferStatus === "offered" && (
              <div className={styles.statusRow}>
                <Typography.Title level={4} className={styles.sectionTitle}>
                  Your Response
                </Typography.Title>
                <Tag
                  color={hasAccepted ? "green" : "orange"}
                  className={styles.statusTag}
                >
                  {hasAccepted ? "Accepted" : "Pending Response"}
                </Tag>
              </div>
            )}

            {admissionStatus && (
              <div className={styles.details}>
                <div>
                  <Typography.Text type="secondary">Student</Typography.Text>
                  <Typography.Title level={5}>{fullName}</Typography.Title>
                </div>
                <div>
                  <Typography.Text type="secondary">
                    Application Number
                  </Typography.Text>
                  <Typography.Title level={5}>
                    {user?.applicationNumber}
                  </Typography.Title>
                </div>
                <div>
                  <Typography.Text type="secondary">Program</Typography.Text>
                  <Typography.Title level={5}>
                    {user?.programName}
                  </Typography.Title>
                </div>
                <div>
                  <Typography.Text type="secondary">Area of Specialization</Typography.Text>
                  <Typography.Title level={5}>
                    {user?.specialization}
                  </Typography.Title>
                </div>
                <div>
                  <Typography.Text type="secondary">
                    Degree Type
                  </Typography.Text>
                  <Typography.Title level={5}>
                    {user?.programType}
                  </Typography.Title>
                </div>
                <div>
                  <Typography.Text type="secondary">
                    Mode of Study
                  </Typography.Text>
                  <Typography.Title level={5}>
                    {user?.modeOfStudyName}
                  </Typography.Title>
                </div>
              </div>
            )}

            {hasAccepted && (
              <Alert
                type="success"
                showIcon
                message="Congratulations! You've accepted your admission offer."
                description="We can't wait to welcome you on campus."
                className={styles.alert}
              />
            )}

            {hasAccepted && (
              <Card className={styles.paymentCard} bordered={false}>
                <div className={styles.paymentHeader}>
                  <div>
                    <Typography.Title level={4} className={styles.sectionTitle}>
                      Acceptance Fee
                    </Typography.Title>
                    <Typography.Text type="secondary">
                      Complete payment to secure your admission.
                    </Typography.Text>
                  </div>
                  {hasPaidAcceptanceFee ? (
                    <Tag color="green" className={styles.statusTag}>
                      Paid
                    </Tag>
                  ) : canPayAcceptanceFee ? (
                    <Tag color="orange" className={styles.statusTag}>
                      Pending Payment
                    </Tag>
                  ) : null}
                </div>

                <div className={styles.paymentBody}>
                  <div className={styles.paymentAmount}>
                    <Typography.Text type="secondary">
                      Amount Due
                    </Typography.Text>
                    <Typography.Title level={2} className={styles.paymentValue}>
                      {acceptanceFeeFormatted}
                    </Typography.Title>
                  </div>
                </div>

                <div className={styles.paymentFooter}>
                  <Typography.Text type="secondary">
                    This is a one-time fee required to confirm your spot.
                  </Typography.Text>
                  {!hasPaidAcceptanceFee ? (
                    <Button
                      type="primary"
                      size="large"
                      onClick={handleProcessPayment}
                      loading={isProcessing}
                    >
                      Pay Acceptance Fee
                    </Button>
                  ) : (
                    <Button type="default" size="large" disabled>
                      Payment Received
                    </Button>
                  )}
                </div>
              </Card>
            )}

            {admissionOfferStatus === "rejected" && (
              <Alert
                type="info"
                showIcon
                message="We're sorry you won't be joining us."
                description="Thank you for letting us know. We wish you the very best ahead."
                className={styles.alert}
              />
            )}

            {isActionable && (
              <div className={styles.actions}>
                <Space size={12} wrap>
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleAccept}
                    loading={acceptOfferMutation.isPending}
                  >
                    Accept Admission
                  </Button>
                </Space>

                <div className={styles.rejectSection}>
                  <Typography.Title level={5} className={styles.rejectTitle}>
                    If you&apos;d like to decline, please tell us why.
                  </Typography.Title>
                  <Input.TextArea
                    value={rejectionReason}
                    onChange={(event) => setRejectionReason(event.target.value)}
                    placeholder="Share your reason for rejecting the admission offer..."
                    rows={4}
                    disabled={acceptOfferMutation.isPending}
                  />
                  <div className={styles.rejectFooter}>
                    <Typography.Text type="secondary">
                      Minimum 5 characters.
                    </Typography.Text>
                    <Button
                      type="primary"
                      danger
                      onClick={handleRejectSubmit}
                      disabled={!canReject}
                      loading={acceptOfferMutation.isPending}
                    >
                      Submit Rejection
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      <Modal
        open={acceptModalOpen}
        onCancel={() => setAcceptModalOpen(false)}
        onOk={() => setAcceptModalOpen(false)}
        okText="Continue"
        title="Welcome to the next chapter"
      >
        <Typography.Paragraph>
          Congratulations! Your acceptance has been received. We&apos;ll send
          onboarding details to your email shortly.
        </Typography.Paragraph>
      </Modal>
    </div>
  );
};

export default AdmissionStatus;
